import React, { useState, useMemo, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight, X, Calendar, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useGetOvertimeRequestsQuery } from "../../slices/overtimeApiSlice";
import * as XLSX from "xlsx";
import DateRangePicker from "../../components/DateRangePicker/DateRangePicker";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import { toast } from "sonner";

export default function OvertimeRecords() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [status, setStatus] = useState("All");
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const dateRangeButtonRef = useRef(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedHistoryIndex, setSelectedHistoryIndex] = useState(0);
  const itemsPerPage = 10;

  // Fetch overtime records from backend - user-specific endpoint (only current technician's requests)
  const {
    data: backendData,
    isLoading,
    isError,
    error,
  } = useGetOvertimeRequestsQuery();

  // Date range state (default to last 1 week)
  // Get today's date in Asia/Thimphu timezone
  const getTodayInThimphu = () => {
    const now = new Date();
    const thimphuDate = new Date(
      now.toLocaleString("en-US", { timeZone: "Asia/Thimphu" }),
    );
    thimphuDate.setHours(0, 0, 0, 0);
    return thimphuDate;
  };

  const today = getTodayInThimphu();
  const oneWeekAgo = new Date(today);
  oneWeekAgo.setDate(today.getDate() - 6); // 7 days including today

  // Helper function to format date as YYYY-MM-DD without timezone issues
  const formatDateString = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [startDate, setStartDate] = useState(formatDateString(oneWeekAgo));
  const [endDate, setEndDate] = useState(formatDateString(today));

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportMonthYear, setExportMonthYear] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  // Format backend date (ISO format) to display format
  const formatBackendDate = (isoDateTime) => {
    if (!isoDateTime) return "";
    const date = new Date(isoDateTime);
    const timeStr = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
    const dateStr = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${dateStr} - ${timeStr}`;
  };

  // Format note timestamp
  const formatNoteTimestamp = (timestamp) => {
    if (!timestamp) return "";
    try {
      const date = new Date(timestamp);
      // Add 6 hours to adjust timezone (same as Submitted On logic)
      date.setHours(date.getHours() + 6);

      const dateStr = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      const timeStr = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      return `Note added on: ${dateStr}, ${timeStr}`;
    } catch (error) {
      return "";
    }
  };

  // Format hours - show minutes if not a whole hour, otherwise show hours and minutes
  const formatHours = (totalHours) => {
    if (!totalHours || totalHours === 0) return "0 hrs";

    const hours = Math.floor(totalHours);
    const minutes = Math.round((totalHours - hours) * 60);

    if (hours === 0) {
      return `${minutes} min`;
    } else if (minutes === 0) {
      return `${hours} hr${hours !== 1 ? "s" : ""}`;
    } else {
      return `${hours} hr${hours !== 1 ? "s" : ""} ${minutes} min`;
    }
  };

  // Format date for submission timestamp
  const formatSubmittedDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      // Add 6 hours to adjust timezone
      date.setHours(date.getHours() + 6);

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  // Format date and time for submission timestamp
  const formatSubmittedDateTime = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      // Add 6 hours to adjust timezone
      date.setHours(date.getHours() + 6);

      const dateStr = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      const timeStr = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
      return `${dateStr} at ${timeStr}`;
    } catch (error) {
      return dateString;
    }
  };

  // Map backend data to display format
  const records = useMemo(() => {
    if (!backendData || !Array.isArray(backendData)) return [];

    const statusColorMap = {
      APPROVED: "bg-blue-100 text-blue-700",
      Approved: "bg-blue-100 text-blue-700",
      REJECTED: "bg-red-100 text-red-700",
      Rejected: "bg-red-100 text-red-700",
      PENDING: "bg-yellow-100 text-yellow-700",
      Pending: "bg-yellow-100 text-yellow-700",
      FORWARDED: "bg-gray-100 text-gray-700",
      Forwarded: "bg-gray-100 text-gray-700",
      REAPPLY: "bg-orange-100 text-orange-700",
      "Re-Apply": "bg-orange-100 text-orange-700",
      REAPPLIED: "bg-orange-100 text-orange-700",
      "Re-Applied": "bg-orange-100 text-orange-700",
    };

    const statusDisplay = {
      PENDING: "Pending",
      REAPPLY: "Re-Apply",
      REAPPLIED: "Re-Applied",
      REJECTED: "Rejected",
      FORWARDED: "Forwarded",
      APPROVED: "Approved",
    };

    return backendData
      .filter((item) => item && (item.details || item.startTime)) // Filter out null items
      .map((item) => {
        const displayStatus =
          statusDisplay[item.status] || item.status || "Pending";

        // Handle both new structure (with details object) and fallback structure
        const details = item.details || item;

        // Extract supervisor note and supervisor email from notes array
        let supervisorNote = "";
        let supervisorEmail = "";
        let supervisorTimestamp = "";
        let managerNote = "";
        let managerEmail = "";
        let managerTimestamp = "";
        let isDirectSupervisorAction = false; // Flag for direct supervisor rejection

        if (
          details.notes &&
          Array.isArray(details.notes) &&
          details.notes.length > 0
        ) {
          let lastSupervisorNote = null;
          let lastManagerNote = null;

          details.notes.forEach((note) => {
            if (note.action === "REJECTED") {
              lastManagerNote = note;
            } else if (note.action === "FORWARDED") {
              lastSupervisorNote = note;
            } else if (note.recordedBy === "MANAGER") {
              lastManagerNote = note;
            } else if (note.recordedBy === "SUPERVISOR") {
              lastSupervisorNote = note;
            } else if (note.managerEmail && !lastManagerNote) {
              lastManagerNote = note;
            } else if (note.supervisorEmail && !lastSupervisorNote) {
              lastSupervisorNote = note;
            }
          });

          if (lastSupervisorNote) {
            supervisorNote =
              lastSupervisorNote.note || lastSupervisorNote.reason || "";
            supervisorEmail = (
              lastSupervisorNote.supervisorEmail ||
              lastSupervisorNote.supervisor ||
              ""
            )
              .replace(/@company\.com/g, "")
              .replace(/@company\.com@company\.com/g, "@company.com");
            supervisorTimestamp =
              lastSupervisorNote.createdAt ||
              lastSupervisorNote.recordedAt ||
              lastSupervisorNote.timestamp ||
              "";
          }

          if (lastManagerNote) {
            managerNote = lastManagerNote.note || lastManagerNote.reason || "";
            managerEmail = (
              lastManagerNote.supervisorEmail ||
              lastManagerNote.supervisor ||
              lastManagerNote.managerEmail ||
              ""
            )
              .replace(/@company\.com/g, "")
              .replace(/@company\.com@company\.com/g, "@company.com");
            managerTimestamp =
              lastManagerNote.createdAt ||
              lastManagerNote.recordedAt ||
              lastManagerNote.timestamp ||
              "";

            // Check if this is a direct supervisor action by examining the email
            // If the email contains "supervisor" in it and there's no prior forward action, it's a direct supervisor rejection
            if (
              !lastSupervisorNote &&
              managerEmail &&
              managerEmail.toLowerCase().includes("supervisor")
            ) {
              isDirectSupervisorAction = true;
              // Move manager note to supervisor note for display purposes
              supervisorNote = managerNote;
              supervisorEmail = managerEmail;
              supervisorTimestamp = managerTimestamp;
              managerNote = "";
              managerEmail = "";
              managerTimestamp = "";
            }
          }
        }

        const isDirectManagerAction =
          !supervisorNote &&
          managerNote &&
          (displayStatus === "Rejected" || displayStatus === "Approved");

        return {
          id: details.id || item.overtimeId || item.id,
          employee:
            details.userName || item.employeeName || item.userId || "N/A",
          email: details.userEmail || item.email || item.employeeEmail || "N/A",
          from: formatBackendDate(details.startTime),
          to: formatBackendDate(details.endTime),
          description: details.description || "No description",
          hours: formatHours(details.totalHours || 0),
          status: displayStatus,
          statusColor:
            statusColorMap[item.status] || "bg-gray-100 text-gray-700",
          supervisorNote: supervisorNote,
          supervisorEmail: supervisorEmail,
          supervisorTimestamp: supervisorTimestamp,
          managerNote: managerNote,
          managerEmail: managerEmail,
          managerTimestamp: managerTimestamp,
          isDirectManagerAction: isDirectManagerAction,
          submissionDate:
            details.submittedAt ||
            details.createdAt ||
            details.requestDate ||
            new Date().toISOString(),
        };
      });
  }, [backendData]);

  const formatDateDisplay = (date) => {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Filtered records based on status and sorted by submission date
  const filteredRecords = useMemo(() => {
    const backendStatusMap = {
      Pending: "PENDING",
      "Re-Apply": "REAPPLY",
      "Re-Applied": "REAPPLIED",
      Rejected: "REJECTED",
      Forwarded: "FORWARDED",
      Approved: "APPROVED",
    };

    let filtered = records.filter((item) => {
      const itemBackendStatus = backendStatusMap[item.status] || item.status;
      if (status !== "All" && itemBackendStatus !== status) return false;
      return true;
    });

    // Apply date range filter only if both dates are selected
    if (startDate && endDate) {
      filtered = filtered.filter((item) => {
        try {
          // Extract date from "Nov 10, 2025" format from item.from
          const dateStr = item.from?.split(" - ")[0];
          if (!dateStr) return false;

          const itemDate = new Date(dateStr);
          if (isNaN(itemDate.getTime())) return false; // Invalid date

          itemDate.setHours(0, 0, 0, 0);

          const filterStart = new Date(startDate);
          filterStart.setHours(0, 0, 0, 0);
          const filterEnd = new Date(endDate);
          filterEnd.setHours(23, 59, 59, 999);

          const isInRange = itemDate >= filterStart && itemDate <= filterEnd;
          return isInRange;
        } catch (err) {
          return false;
        }
      });
    }

    // Sort by most recent submission date first
    // Use stable sort with ID as tiebreaker to ensure consistent ordering
    return filtered.sort((a, b) => {
      const dateA = new Date(a.submissionDate);
      const dateB = new Date(b.submissionDate);
      const timeDiff = dateB - dateA;
      // If timestamps are equal, use ID comparison for stability
      if (timeDiff === 0) {
        return (b.id || "").toString().localeCompare((a.id || "").toString());
      }
      return timeDiff;
    });
  }, [records, status, startDate, endDate]);

  // Pagination
  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
  const totalResults = filteredRecords.length;
  const paginatedRecords = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredRecords.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredRecords, currentPage]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Helper function to format date for display
  const formatDate = (dateString, short = false) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (short) {
      // dd/mm/yyyy format for compact display
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Status options for CustomSelect
  const statusOptions = [
    { value: "All", label: "All" },
    { value: "PENDING", label: "Pending" },
    { value: "REAPPLY", label: "Re-Apply" },
    { value: "REAPPLIED", label: "Re-Applied" },
    { value: "REJECTED", label: "Rejected" },
    { value: "FORWARDED", label: "Forwarded" },
    { value: "APPROVED", label: "Approved" },
  ];

  const renderPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    // Adjust startPage if we're near the end
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    // Previous button
    if (currentPage > 1) {
      pages.push(
        <button
          key="prev"
          onClick={() => handlePageChange(currentPage - 1)}
          className="px-3 py-1 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded transition"
          title="Previous page"
        >
          <ChevronLeft size={16} />
        </button>,
      );
    }

    // First page button
    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          onClick={() => handlePageChange(1)}
          className="w-8 h-8 flex items-center justify-center rounded text-gray-600 hover:bg-gray-100 transition"
        >
          1
        </button>,
      );

      // Ellipsis if there's a gap
      if (startPage > 2) {
        pages.push(
          <span
            key="ellipsis1"
            className="w-8 h-8 flex items-center justify-center text-gray-600"
          >
            ...
          </span>,
        );
      }
    }

    // Page number buttons
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`w-8 h-8 flex items-center justify-center rounded transition ${
            currentPage === i
              ? "bg-blue-500 text-white font-semibold"
              : "text-gray-600 hover:bg-gray-100"
          }`}
        >
          {i}
        </button>,
      );
    }

    // Last page button
    if (endPage < totalPages) {
      // Ellipsis if there's a gap
      if (endPage < totalPages - 1) {
        pages.push(
          <span
            key="ellipsis2"
            className="w-8 h-8 flex items-center justify-center text-gray-600"
          >
            ...
          </span>,
        );
      }

      pages.push(
        <button
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
          className="w-8 h-8 flex items-center justify-center rounded text-gray-600 hover:bg-gray-100 transition"
        >
          {totalPages}
        </button>,
      );
    }

    // Next button
    if (currentPage < totalPages) {
      pages.push(
        <button
          key="next"
          onClick={() => handlePageChange(currentPage + 1)}
          className="px-3 py-1 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded transition"
          title="Next page"
        >
          <ChevronRight size={16} />
        </button>,
      );
    }

    return pages;
  };

  // Export approved overtimes to Excel
  const handleExportToExcel = () => {
    // Filter only approved records
    const approvedRecords = records.filter(
      (record) => record.status === "Approved",
    );

    if (approvedRecords.length === 0) {
      toast.warning("No approved overtime records to export");
      return;
    }

    // Open export modal
    setShowExportModal(true);
  };

  // Handle actual export with month filtering
  const handleExportApprovedOvertimes = async () => {
    // Validate month selection
    if (!exportMonthYear) {
      toast.warning("Please select a month to export");
      return;
    }

    // Get approved records
    const approvedRecords = records.filter(
      (record) => record.status === "Approved",
    );

    if (approvedRecords.length === 0) {
      toast.warning("No approved overtime records to export");
      return;
    }

    // Filter by the selected export month
    const exportMonth = new Date(exportMonthYear);
    const monthStart = new Date(
      exportMonth.getFullYear(),
      exportMonth.getMonth(),
      1,
    );
    const monthEnd = new Date(
      exportMonth.getFullYear(),
      exportMonth.getMonth() + 1,
      0,
    );

    const monthFilteredRecords = approvedRecords.filter((record) => {
      try {
        const dateStr = record.from?.split(" - ")[0];
        if (!dateStr) return false;

        const recordDate = new Date(dateStr);
        if (isNaN(recordDate.getTime())) return false;

        recordDate.setHours(0, 0, 0, 0);
        return recordDate >= monthStart && recordDate <= monthEnd;
      } catch (err) {
        return false;
      }
    });

    if (monthFilteredRecords.length === 0) {
      toast.warning(
        `No approved overtime records found for ${exportMonth.toLocaleDateString(
          "en-US",
          { month: "long", year: "numeric" },
        )}`,
      );
      return;
    }

    setIsExporting(true);

    try {
      // Prepare data for Excel in the format shown in the image
      const excelData = monthFilteredRecords.map((record, index) => {
        // Extract date and times from the formatted strings
        const dateAndTime = record.from.split(" - ");
        const date = dateAndTime[0] || "";
        const fromTime = dateAndTime[1] || "";
        const toTime = record.to.split(" - ")[1] || "";

        return {
          "No.": index + 1,
          "From Date/Time": date + " " + fromTime,
          "Number of Hours": record.hours,
          "To Date/Time": date + " " + toTime,
          "Hourly Rate": "", // Keep empty as per requirement
          Amount: "", // Keep empty as per requirement
        };
      });

      // Create a new workbook
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      worksheet["!cols"] = [
        { wch: 8 }, // No.
        { wch: 25 }, // From Date/Time
        { wch: 18 }, // Number of Hours
        { wch: 25 }, // To Date/Time
        { wch: 15 }, // Hourly Rate
        { wch: 15 }, // Amount
      ];

      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, "Overtime Details");

      // Generate filename with month and year
      const monthYearStr = exportMonth
        .toLocaleDateString("en-US", { month: "short", year: "numeric" })
        .replace(" ", "-");
      XLSX.writeFile(workbook, `Overtime_Details_${monthYearStr}.xlsx`);

      setShowExportModal(false);
      setExportMonthYear(null);
      setIsExporting(false);

      toast.success("File downloaded");
    } catch (error) {
      setIsExporting(false);
      toast.error("Failed to export overtime records");
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header with chevron and date range */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <button
            onClick={() => navigate("/technician/overtime")}
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-zinc-600 hover:bg-zinc-100 hover:text-zinc-800 transition-colors"
            aria-label="Go back"
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <h1 className="text-xl sm:text-2xl font-bold text-zinc-800">
            Overtime Records
          </h1>
        </div>
        <div className="flex gap-2 sm:gap-3">
          <button
            onClick={handleExportToExcel}
            className="bg-white border border-gray-300 rounded-lg px-3 py-2 sm:px-4 sm:py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            title="Export approved overtime records"
          >
            <Download size={16} className="sm:w-5 sm:h-5" />
            <span className="hidden sm:inline">Export</span>
            <span className="sm:hidden">Export</span>
          </button>
          <button
            onClick={() => navigate("/technician/overtime/apply")}
            className="bg-white border border-gray-300 rounded-lg px-3 py-2 sm:px-4 sm:py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-50"
          >
            Apply Overtime
          </button>
        </div>
      </div>

      {/* Records Section */}
      <div className="bg-white rounded-lg border">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-zinc-800">Records</h2>
              <p className="text-sm text-zinc-600 mt-1">
                All your overtime records
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <CustomSelect
                options={statusOptions}
                value={status}
                onChange={(value) => {
                  setStatus(value);
                  setCurrentPage(1);
                }}
                placeholder="All"
                minWidth={150}
              />
              {/* Date Range Filter */}
              <div className="relative">
                <button
                  ref={dateRangeButtonRef}
                  onClick={() => setShowDateRangePicker(!showDateRangePicker)}
                  className="bg-white border border-gray-300 rounded-lg px-3 py-2 sm:px-4 sm:py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-50 whitespace-nowrap flex-shrink-0"
                >
                  {startDate && endDate ? (
                    <>
                      <span className="hidden lg:inline">
                        {formatDate(startDate)} - {formatDate(endDate)}
                      </span>
                      <span className="lg:hidden">
                        {formatDate(startDate, true)} -{" "}
                        {formatDate(endDate, true)}
                      </span>
                    </>
                  ) : (
                    "Select date range"
                  )}
                </button>
                <DateRangePicker
                  show={showDateRangePicker}
                  onClose={() => setShowDateRangePicker(false)}
                  startDate={startDate}
                  endDate={endDate}
                  onDateRangeSelect={({ start, end }) => {
                    if (start && end) {
                      setStartDate(start);
                      setEndDate(end);
                    }
                  }}
                  buttonRef={dateRangeButtonRef}
                  allowFutureDates={true}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <div className="overflow-x-auto min-w-0">
            {isLoading ? (
              <div className="p-6 text-center text-gray-600">Loading...</div>
            ) : isError ? (
              <div className="p-6 text-center">
                <p className="text-red-600 font-semibold">
                  Failed to load overtime records
                </p>
                <p className="text-gray-600 text-sm mt-2">
                  {error?.status && `Error: ${error.status}`}
                  {error?.data?.message && ` - ${error.data.message}`}
                </p>
              </div>
            ) : paginatedRecords.length === 0 ? (
              <div className="p-6 text-center text-gray-600">
                {records.length === 0
                  ? "No overtime requests"
                  : "No overtime records found for the selected filters"}
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-zinc-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                      From Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                      To Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                      Hours
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-zinc-200">
                  {isLoading ? (
                    <tr>
                      <td
                        colSpan="6"
                        className="px-6 py-8 text-center text-zinc-500"
                      >
                        Loading records...
                      </td>
                    </tr>
                  ) : isError ? (
                    <tr>
                      <td
                        colSpan="6"
                        className="px-6 py-8 text-center text-red-500"
                      >
                        Error loading records:{" "}
                        {error?.data?.message ||
                          error?.message ||
                          "Please try again."}
                      </td>
                    </tr>
                  ) : paginatedRecords.length > 0 ? (
                    paginatedRecords.map((item, index) => (
                      <tr
                        key={item.id || index}
                        className="hover:bg-zinc-50 cursor-pointer transition-colors"
                        onClick={() => setSelectedRecord(item)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-zinc-900">
                          {item.from.split(" - ")[0]}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                          {item.from.split(" - ")[1]
                            ? `${item.from
                                .split(" - ")[1]
                                .substring(0, 5)} ${item.from
                                .split(" - ")[1]
                                .substring(9, 11)}`
                            : "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                          {item.to.split(" - ")[1]
                            ? `${item.to
                                .split(" - ")[1]
                                .substring(0, 5)} ${item.to
                                .split(" - ")[1]
                                .substring(9, 11)}`
                            : "-"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                          {item.hours}
                        </td>
                        <td className="px-6 py-4 text-sm text-zinc-600">
                          <div
                            className="truncate max-w-xs"
                            title={item.description}
                          >
                            {item.description.length > 35
                              ? `${item.description.substring(0, 35)}...`
                              : item.description}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium ${item.statusColor}`}
                          >
                            {item.status}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="6"
                        className="px-6 py-8 text-center text-zinc-500"
                      >
                        No records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 7) {
                    pageNum = i + 1;
                  } else if (currentPage <= 4) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 3) {
                    pageNum = totalPages - 6 + i;
                  } else {
                    pageNum = currentPage - 3 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === pageNum
                          ? "bg-zinc-900 text-white"
                          : "text-zinc-600 hover:bg-zinc-100"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                {totalPages > 7 && currentPage < totalPages - 3 && (
                  <>
                    <span className="px-2 text-zinc-400">...</span>
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      className="px-3 py-1 rounded-lg text-sm font-medium text-zinc-600 hover:bg-zinc-100 transition-colors"
                    >
                      {totalPages}
                    </button>
                  </>
                )}
              </div>
              <button
                onClick={() =>
                  handlePageChange(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="text-sm text-zinc-600 font-medium">
              {totalResults} Results
            </div>
          </div>
        )}
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => {
              setShowExportModal(false);
              setExportMonthYear(null);
            }}
          />
          <div className="fixed inset-0 flex items-center justify-center z-50 p-3 sm:p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              {/* Header */}
              <div className="flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                  Export Overtime Records
                </h2>
                <button
                  onClick={() => {
                    setShowExportModal(false);
                    setExportMonthYear(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Content */}
              <div className="px-4 sm:px-6 py-4 sm:py-5 space-y-4">
                {/* Month Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Month <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="month"
                    value={
                      exportMonthYear
                        ? `${exportMonthYear.getFullYear()}-${String(
                            exportMonthYear.getMonth() + 1,
                          ).padStart(2, "0")}`
                        : ""
                    }
                    onChange={(e) => {
                      if (e.target.value) {
                        const [year, month] = e.target.value.split("-");
                        setExportMonthYear(
                          new Date(parseInt(year), parseInt(month) - 1, 1),
                        );
                      } else {
                        setExportMonthYear(null);
                      }
                    }}
                    min={`${new Date().getFullYear()}-01`}
                    max={`${new Date().getFullYear()}-${String(
                      new Date().getMonth() + 1,
                    ).padStart(2, "0")}`}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Only approved records from the selected month will be
                    exported. Select a month from current year only.
                  </p>
                </div>
              </div>

              {/* Footer Buttons */}
              <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowExportModal(false);
                    setExportMonthYear(null);
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExportApprovedOvertimes}
                  disabled={!exportMonthYear || isExporting}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isExporting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download size={16} />
                      Export
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Detail Modal */}
      {selectedRecord && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setSelectedRecord(null)}
            style={{ zIndex: 9998 }}
          />
          <div
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ zIndex: 9999 }}
          >
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header with Status Badge and Close */}
              <div className="flex items-center justify-between px-6 pt-4">
                <span
                  className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${selectedRecord.statusColor}`}
                >
                  {selectedRecord.status}
                </span>
                <button
                  onClick={() => setSelectedRecord(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              {/* User Profile Section - Redesigned */}
              <div className="px-6 py-6 border-b border-gray-200">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-semibold text-2xl">
                      {selectedRecord.userName?.charAt(0).toUpperCase() ||
                        selectedRecord.employee?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900">
                      {selectedRecord.userName || selectedRecord.employee}
                    </h2>
                    <p className="text-lg font-medium text-gray-700">
                      {selectedRecord.userEmail || selectedRecord.email}
                    </p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-4 space-y-0">
                {/* Description Row */}
                <div className="py-4 border-b border-gray-200">
                  <span className="inline-block bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-1 rounded mb-2">
                    Description
                  </span>
                  <p className="text-gray-700 text-sm">
                    {selectedRecord.description}
                  </p>
                </div>

                {/* Date Information Section */}
                <div className="mb-3 py-4">
                  <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3">
                    <span className="text-xs sm:text-sm font-medium text-gray-700">
                      Date:
                    </span>
                    <span className="text-gray-900 text-sm">
                      {selectedRecord.from.split(" - ")[0]}
                    </span>
                  </div>

                  <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3">
                    <span className="text-xs sm:text-sm font-medium text-gray-700">
                      From:
                    </span>
                    <span className="text-gray-900 text-sm">
                      {selectedRecord.from.split(" - ")[1]}
                    </span>
                  </div>

                  <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3">
                    <span className="text-xs sm:text-sm font-medium text-gray-700">
                      To:
                    </span>
                    <span className="text-gray-900 text-sm">
                      {selectedRecord.to.split(" - ")[1]}
                    </span>
                  </div>

                  <div className="flex justify-between items-center border-b border-gray-300 pb-2">
                    <span className="text-xs sm:text-sm font-medium text-gray-700">
                      Hours
                    </span>
                    <span className="text-gray-900 text-sm">
                      {selectedRecord.hours}
                    </span>
                  </div>

                  <div className="flex justify-between items-center border-b border-gray-300 pb-2 mt-3">
                    <span className="text-xs sm:text-sm font-medium text-gray-700">
                      Submitted On:
                    </span>
                    <span className="text-gray-600 text-sm">
                      {formatSubmittedDateTime(selectedRecord.submissionDate)}
                    </span>
                  </div>
                </div>

                {/* Supervisor Note - Show for Forwarded, Approved, Rejected, or Re-Apply status */}
                {(selectedRecord.status === "Forwarded" ||
                  selectedRecord.status === "Approved" ||
                  selectedRecord.status === "Rejected" ||
                  selectedRecord.status === "Re-Apply") &&
                  selectedRecord.supervisorNote && (
                    <div className="py-4 border-b border-gray-200">
                      <div
                        className={`rounded-lg p-3 sm:p-4 ${
                          selectedRecord.status === "Rejected" &&
                          !selectedRecord.managerNote
                            ? "bg-red-50 border border-red-200"
                            : selectedRecord.status === "Rejected"
                              ? "bg-blue-50 border border-blue-200"
                              : selectedRecord.status === "Forwarded" ||
                                  selectedRecord.status === "Approved"
                                ? "bg-blue-50 border border-blue-200"
                                : "bg-orange-50 border border-orange-200"
                        }`}
                      >
                        {/* Supervisor Info */}
                        <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
                          <div
                            className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                              selectedRecord.status === "Rejected" &&
                              !selectedRecord.managerNote
                                ? "bg-red-200"
                                : selectedRecord.status === "Rejected"
                                  ? "bg-blue-200"
                                  : selectedRecord.status === "Forwarded" ||
                                      selectedRecord.status === "Approved"
                                    ? "bg-blue-200"
                                    : "bg-orange-200"
                            }`}
                          >
                            <span
                              className={`font-semibold text-xs sm:text-sm ${
                                selectedRecord.status === "Rejected" &&
                                !selectedRecord.managerNote
                                  ? "text-red-700"
                                  : selectedRecord.status === "Rejected"
                                    ? "text-blue-700"
                                    : selectedRecord.status === "Forwarded" ||
                                        selectedRecord.status === "Approved"
                                      ? "text-blue-700"
                                      : "text-orange-700"
                              }`}
                            >
                              {selectedRecord.supervisorEmail
                                ?.charAt(0)
                                .toUpperCase() || "S"}
                            </span>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                              Supervisor
                            </h4>
                            <p className="text-xs text-gray-600">
                              {selectedRecord.supervisorEmail}
                            </p>
                          </div>
                        </div>

                        {/* Note Content */}
                        <div className="ml-0 sm:ml-11">
                          <p
                            className={`text-xs sm:text-sm font-medium mb-1 ${
                              selectedRecord.status === "Rejected" &&
                              !selectedRecord.managerNote
                                ? "text-red-700"
                                : selectedRecord.status === "Rejected"
                                  ? "text-blue-700"
                                  : selectedRecord.status === "Forwarded" ||
                                      selectedRecord.status === "Approved"
                                    ? "text-blue-700"
                                    : "text-orange-700"
                            }`}
                          >
                            {selectedRecord.status === "Rejected" &&
                            !selectedRecord.managerNote
                              ? "Rejection Reason:"
                              : selectedRecord.status === "Forwarded"
                                ? "Forwarded Reason:"
                                : selectedRecord.status === "Approved"
                                  ? "Supervisor's Note:"
                                  : selectedRecord.status === "Rejected"
                                    ? "Forwarded Reason:"
                                    : "Supervisor's Request:"}
                          </p>
                          <p
                            className={`text-sm sm:text-base ${
                              selectedRecord.status === "Rejected" &&
                              !selectedRecord.managerNote
                                ? "text-red-900"
                                : selectedRecord.status === "Rejected"
                                  ? "text-blue-900"
                                  : selectedRecord.status === "Forwarded" ||
                                      selectedRecord.status === "Approved"
                                    ? "text-blue-900"
                                    : "text-orange-900"
                            }`}
                          >
                            {selectedRecord.supervisorNote}
                          </p>
                          {selectedRecord.supervisorTimestamp && (
                            <p className="text-xs text-gray-500 mt-2">
                              {formatNoteTimestamp(
                                selectedRecord.supervisorTimestamp,
                              )}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                {/* Direct Manager Rejection (No Supervisor) */}
                {selectedRecord.status === "Rejected" &&
                  selectedRecord.isDirectManagerAction &&
                  selectedRecord.managerNote && (
                    <div className="py-4 border-b border-gray-200">
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
                        {/* Manager Info */}
                        <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-red-200">
                            <span className="font-semibold text-xs sm:text-sm text-red-700">
                              {selectedRecord.managerEmail
                                ?.charAt(0)
                                .toUpperCase() || "M"}
                            </span>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                              Manager
                            </h4>
                            <p className="text-xs text-gray-600">
                              {selectedRecord.managerEmail}
                            </p>
                          </div>
                        </div>

                        {/* Note Content */}
                        <div className="ml-0 sm:ml-11">
                          <p className="text-xs sm:text-sm font-medium mb-1 text-red-700">
                            Rejection Note:
                          </p>
                          <p className="text-sm sm:text-base text-red-900">
                            {selectedRecord.managerNote}
                          </p>
                          {selectedRecord.managerTimestamp && (
                            <p className="text-xs text-gray-500 mt-2">
                              {formatNoteTimestamp(
                                selectedRecord.managerTimestamp,
                              )}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                {/* Manager Note Display - Show for Approved/Rejected status (when supervisor involved) */}
                {(selectedRecord.status === "Approved" ||
                  selectedRecord.status === "Rejected") &&
                  selectedRecord.managerNote &&
                  !selectedRecord.isDirectManagerAction && (
                    <div className="py-4 border-b border-gray-200">
                      <div
                        className={`rounded-lg p-3 sm:p-4 ${
                          selectedRecord.status === "Approved"
                            ? "bg-green-50 border border-green-200"
                            : "bg-red-50 border border-red-200"
                        }`}
                      >
                        {/* Manager Info */}
                        <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
                          <div
                            className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                              selectedRecord.status === "Approved"
                                ? "bg-green-200"
                                : "bg-red-200"
                            }`}
                          >
                            <span
                              className={`font-semibold text-xs sm:text-sm ${
                                selectedRecord.status === "Approved"
                                  ? "text-green-700"
                                  : "text-red-700"
                              }`}
                            >
                              {selectedRecord.managerEmail
                                ?.charAt(0)
                                .toUpperCase() || "M"}
                            </span>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                              Manager
                            </h4>
                            <p className="text-xs text-gray-600">
                              {selectedRecord.managerEmail}
                            </p>
                          </div>
                        </div>

                        {/* Note Content */}
                        <div className="ml-0 sm:ml-11">
                          <p
                            className={`text-xs sm:text-sm font-medium mb-1 ${
                              selectedRecord.status === "Approved"
                                ? "text-green-700"
                                : "text-red-700"
                            }`}
                          >
                            {selectedRecord.status === "Approved"
                              ? "Manager's Approval Note:"
                              : "Manager's Rejection Note:"}
                          </p>
                          <p
                            className={`text-sm sm:text-base ${
                              selectedRecord.status === "Approved"
                                ? "text-green-900"
                                : "text-red-900"
                            }`}
                          >
                            {selectedRecord.managerNote}
                          </p>
                          {selectedRecord.managerTimestamp && (
                            <p className="text-xs text-gray-500 mt-2">
                              {formatNoteTimestamp(
                                selectedRecord.managerTimestamp,
                              )}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                {/* Direct Manager Approval (No Supervisor) */}
                {selectedRecord.status === "Approved" &&
                  selectedRecord.isDirectManagerAction &&
                  selectedRecord.managerNote && (
                    <div className="py-4 border-b border-gray-200">
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
                        {/* Manager Info */}
                        <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-green-200">
                            <span className="font-semibold text-xs sm:text-sm text-green-700">
                              {selectedRecord.managerEmail
                                ?.charAt(0)
                                .toUpperCase() || "M"}
                            </span>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                              Manager
                            </h4>
                            <p className="text-xs text-gray-600">
                              {selectedRecord.managerEmail}
                            </p>
                          </div>
                        </div>

                        {/* Note Content */}
                        <div className="ml-0 sm:ml-11">
                          <p className="text-xs sm:text-sm font-medium mb-1 text-green-700">
                            Approval Note:
                          </p>
                          <p className="text-sm sm:text-base text-green-900">
                            {selectedRecord.managerNote}
                          </p>
                          {selectedRecord.managerTimestamp && (
                            <p className="text-xs text-gray-500 mt-2">
                              {formatNoteTimestamp(
                                selectedRecord.managerTimestamp,
                              )}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                {/* Status Message - Show when no notes */}
                {selectedRecord.status === "Pending" && (
                  <div className="flex items-center justify-center gap-2 text-gray-600 py-4 border-t">
                    <div className="w-4 h-4 rounded-full border-2 border-gray-400 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-gray-400" />
                    </div>
                    <span className="text-sm">Currently in review</span>
                  </div>
                )}

                {selectedRecord.status === "Re-Applied" && (
                  <div className="flex items-center justify-center gap-2 text-gray-600 py-4 border-t">
                    <div className="w-4 h-4 rounded-full border-2 border-gray-400 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-gray-400" />
                    </div>
                    <span className="text-sm">Currently in review</span>
                  </div>
                )}

                {/* Previous Data Dropdown - Only for Re-Applied status */}
                {selectedRecord.status === "Re-Applied" &&
                  selectedRecord.history &&
                  selectedRecord.history.length > 1 && (
                    <div className="py-4 border-t border-gray-200 px-6">
                      <label className="text-xs font-medium text-gray-600 mb-2 block">
                        View Previous Submission:
                      </label>
                      <select
                        value={selectedHistoryIndex}
                        onChange={(e) =>
                          setSelectedHistoryIndex(parseInt(e.target.value))
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {selectedRecord.history.map((record, idx) => (
                          <option key={idx} value={idx}>
                            {record.recordedBy === "INITIAL"
                              ? "Initial Request"
                              : "Reapply"}{" "}
                            - From:{" "}
                            {record.startTime?.split("T")[1]?.substring(0, 5) ||
                              "N/A"}{" "}
                            To:{" "}
                            {record.endTime?.split("T")[1]?.substring(0, 5) ||
                              "N/A"}
                          </option>
                        ))}
                      </select>

                      {/* Display Old Data */}
                      {selectedRecord.history[selectedHistoryIndex] && (
                        <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
                          <p className="text-xs font-medium text-gray-700 mb-2">
                            Previous Data:
                          </p>
                          <div className="space-y-1 text-xs text-gray-600">
                            <div className="flex justify-between">
                              <span>From:</span>
                              <span className="font-medium">
                                {selectedRecord.history[
                                  selectedHistoryIndex
                                ].startTime
                                  ?.split("T")[1]
                                  ?.substring(0, 5) || "N/A"}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>To:</span>
                              <span className="font-medium">
                                {selectedRecord.history[
                                  selectedHistoryIndex
                                ].endTime
                                  ?.split("T")[1]
                                  ?.substring(0, 5) || "N/A"}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Hours:</span>
                              <span className="font-medium">
                                {selectedRecord.history[selectedHistoryIndex]
                                  .totalHours || "N/A"}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
              </div>

              {/* Footer - Action Buttons - Only show for Re-Apply status */}
              {selectedRecord.status === "Re-Apply" && (
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex gap-3 justify-center">
                  <button
                    onClick={() => setSelectedRecord(null)}
                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
                  >
                    Close
                  </button>
                  <button
                    onClick={() =>
                      navigate("/technician/overtime/reapply", {
                        state: { selectedRecord, previousHistoryData: null },
                      })
                    }
                    className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 font-medium"
                  >
                    Resubmit
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
